import {ActivitiesEmissionsResponse} from "../../api/model/emissions";
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL, STATUS_ORDER} from "../../api/model/common";
import {sumActivitiesEmissionResponseTotalEmissionsKgCo2} from "./co2-getter";
import {
    allActivitiesCalculatedSuccessful,
    allActivitiesCalculatedWithError,
    oneActivityCalculatedWithError
} from "../check/emissions";
import {AllActivityTypes, AllActivityTypesArray} from "../../types-enums/activity";
import {getReturnActivity} from "./activities";
import {isActivityWithReturnRouteProps} from "../check/type-guards";


export interface TotalCo2KgEEmissionsAndCalculationStatusProps {
    status: CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL | null,
    emissionKgCO2eTotal: number | null
}

/**
 * Determines the status of backend calculations based on the provided activity emissions response.
 *
 * @param {ActivitiesEmissionsResponse | null} calculatedActivities - The response containing the details of calculated activities or null.
 * @returns {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL | null} The overall calculation status:
 * - `success` if all activities were calculated successfully.
 * - `failed` if all activities encountered errors during calculation.
 * - `success_with_errors` if at least one activity was calculated with an error and the rest of the calculation was successfull.
 * - `null` if no applicable status can be determined.
 */
export const getBackendCalculationStatus = (calculatedActivities: ActivitiesEmissionsResponse | null): CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL | null => {
    if (allActivitiesCalculatedSuccessful(calculatedActivities)) return CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success
    if (allActivitiesCalculatedWithError(calculatedActivities)) return CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed
    if (oneActivityCalculatedWithError(calculatedActivities)) return CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors
    return null
}

/**
 * Calculates and retrieves the total CO2 equivalent emissions in kilograms and the calculation status
 * based on the provided activity data.
 *
 * @param {AllActivityTypes | null} activity - The activity data to calculate emissions for. If null, a default result is returned.
 * @returns {TotalCo2KgEEmissionsAndCalculationStatusProps} An object containing the calculation status and the total CO2 equivalent emissions (in kilograms).
 */
export const getTotalCo2KgEEmissionsAndCalculationStatus = (activity: AllActivityTypes | null): TotalCo2KgEEmissionsAndCalculationStatusProps => {
    if (activity === null) return {
        status: null,
        emissionKgCO2eTotal: 0
    }
    const {common: {totalEmissions}} = activity
    return {
        status: getBackendCalculationStatus(totalEmissions),
        emissionKgCO2eTotal: sumActivitiesEmissionResponseTotalEmissionsKgCo2(activity, true)
    }
}

/**
 * Calculates the total CO2 emissions in kilograms (kgCO2e) and determines the combined calculation status,
 * including the emissions and status for any applicable return activity.
 *
 * This function takes an activity and its associated activities array, computes the total emissions
 * for the given activity, and if applicable, includes the emissions from a return activity. The resulting
 * status is determined by comparing the status of the activity and its return counterpart.
 *
 * @param {AllActivityTypes} activity - The activity for which emissions and calculation status are determined.
 * @param {AllActivityTypesArray} activities - The array of all activity objects to check for a return activity.
 * @returns {TotalCo2KgEEmissionsAndCalculationStatusProps} An object containing the total emissions (in kgCO2e)
 * and the combined calculation status.
 */
export const getTotalCo2KgEEmissionsAndCalculationStatusIncludingReturnActivity = (activity: AllActivityTypes, activities: AllActivityTypesArray): TotalCo2KgEEmissionsAndCalculationStatusProps => {
    let {emissionKgCO2eTotal, status} = getTotalCo2KgEEmissionsAndCalculationStatus(activity)
    if (isActivityWithReturnRouteProps(activity)) {
        const returnActivity = getReturnActivity(activity, activities)
        if (returnActivity) {
            const {
                emissionKgCO2eTotal: emissionKgCO2eTotalReturnActivity,
                status: statusReturnActivity
            } = getTotalCo2KgEEmissionsAndCalculationStatus(returnActivity)
            if (emissionKgCO2eTotal === null) {
                emissionKgCO2eTotal = emissionKgCO2eTotalReturnActivity
            } else {
                if (emissionKgCO2eTotalReturnActivity !== null) emissionKgCO2eTotal += emissionKgCO2eTotalReturnActivity
            }
            if (!status && !statusReturnActivity) return {emissionKgCO2eTotal, status: null}
            if (!status) return {emissionKgCO2eTotal, status: statusReturnActivity}
            if (!statusReturnActivity) return {emissionKgCO2eTotal, status}

            status = STATUS_ORDER[status] >= STATUS_ORDER[statusReturnActivity] ? status : statusReturnActivity;
        }
    }
    return {emissionKgCO2eTotal, status}
}